import type { PropBehaviour, TileRef, WorldDocument } from "../document/types.ts";
import type { BehaviourPreset } from "../editor/presets.ts";
import type { PropTemplate } from "../editor/templates.ts";
import { tilePropsTilesetId } from "./types.ts";

/**
 * Where a tile prop is used (ADR-102 decision 24). A retirement is confirmed, not refused, and the confirmation names
 * what the editor can see: the open world's placements, a template's pieces and a preset's frame. The frames that
 * count are a prop's own, a behaviour's state frames and a behaviour's transition animations, in the order a
 * document holds them, so the first use is the first a person would find.
 */

/**
 * Which frame of a placement or a piece uses the entry: its own, one of its behaviour's states, or one of its
 * behaviour's transition animations.
 */
export type TilePropSlot = "frame" | "state" | "animation";

/**
 * One use. A world's names the map, layer and prop; a template's the piece by its index; a preset's nothing but the
 * slot. `name` is the state or animation, when the slot is one.
 */
export type TilePropUse =
  | { readonly source: "world"; readonly slot: TilePropSlot; readonly name?: string; readonly mapId: string; readonly layerId: string; readonly propId: string }
  | { readonly source: "template"; readonly slot: TilePropSlot; readonly name?: string; readonly piece: number }
  | { readonly source: "preset"; readonly slot: TilePropSlot; readonly name?: string };

/**
 * A document that can use a tile prop: a world, a template or a preset.
 */
export type TilePropSource =
  | { readonly kind: "world"; readonly document: WorldDocument }
  | { readonly kind: "template"; readonly template: PropTemplate }
  | { readonly kind: "preset"; readonly preset: BehaviourPreset };

/**
 * One frame a prop or piece draws and where in it, before the document place is added.
 */
interface Slot {
  readonly slot: TilePropSlot;
  readonly name?: string;
}

/**
 * The slots of a prop or piece that draw a frame: its own, then its behaviour's states and animations, each by key.
 *
 * @param frame The own frame, or none for a preset's behaviour alone.
 * @param behaviour The behaviour, if any.
 * @param matches Whether a reference is the entry's frame.
 * @returns The slots that draw the entry's frame.
 */
function slotsOf(frame: TileRef | undefined, behaviour: PropBehaviour | undefined, matches: (ref: TileRef) => boolean): Slot[] {
  const slots: Slot[] = frame !== undefined && matches(frame) ? [{ slot: "frame" }] : [];
  for (const [slot, refs] of [["state", behaviour?.frames], ["animation", behaviour?.animations]] as const) {
    for (const name of Object.keys(refs ?? {}).sort()) {
      if (matches((refs as { readonly [key: string]: TileRef })[name] as TileRef)) {
        slots.push({ slot, name });
      }
    }
  }
  return slots;
}

/**
 * Every use of an entry in a document, in the document's order: a world map by map, layer by layer and prop by
 * prop; a template piece by piece; a preset once.
 *
 * @param source The document.
 * @param pack The entry's pack.
 * @param id The entry's id.
 * @returns The uses, none when nothing draws the entry.
 */
export function tilePropUses(source: TilePropSource, pack: string, id: number): TilePropUse[] {
  const tileset = tilePropsTilesetId(pack);
  const matches = (ref: TileRef): boolean => ref.tileset === tileset && ref.tile === id;
  if (source.kind === "world") {
    return source.document.maps.flatMap((map) =>
      map.layers.flatMap((layer) =>
        layer.kind !== "prop" ? [] : layer.props.flatMap((prop) => slotsOf(prop.frame, prop.behaviour, matches).map((slot): TilePropUse => ({ source: "world", ...slot, mapId: map.id, layerId: layer.id, propId: prop.id }))),
      ),
    );
  }
  if (source.kind === "template") {
    return source.template.pieces.flatMap((piece, index) => slotsOf(piece.frame, piece.behaviour, matches).map((slot): TilePropUse => ({ source: "template", ...slot, piece: index })));
  }
  return slotsOf(source.preset.frame, source.preset.behaviour, matches).map((slot): TilePropUse => ({ source: "preset", ...slot }));
}

/**
 * The first use of an entry in a document, in the document's order.
 *
 * @param source The document.
 * @param pack The entry's pack.
 * @param id The entry's id.
 * @returns The use, or undefined when nothing draws the entry.
 */
export function firstTilePropUse(source: TilePropSource, pack: string, id: number): TilePropUse | undefined {
  return tilePropUses(source, pack, id)[0];
}
